import React from "react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const TONE_CLASS = {
  default:  { ring: "border-border",                       accent: "text-foreground",          fill: "hsl(var(--primary))"     },
  primary:  { ring: "border-[hsl(var(--primary)/0.35)]",   accent: "text-[hsl(var(--primary))]", fill: "hsl(var(--primary))"   },
  success:  { ring: "border-border",                       accent: "sev-low",                    fill: "hsl(var(--success))"   },
  warning:  { ring: "border-border",                       accent: "sev-medium",                 fill: "hsl(var(--warning))"   },
  danger:   { ring: "border-[hsl(var(--destructive)/0.4)]", accent: "sev-critical",              fill: "hsl(var(--destructive))" },
};

const Sparkline = ({ data, color }) => {
  if (!data || data.length < 2) {
    return <div className="h-8 w-full" />;
  }
  const series = data.map((v, i) => ({ i, v: Number(v) || 0 }));
  return (
    <div className="h-8 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`spk-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={color} stopOpacity={0.45} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#spk-${color})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const TrendBadge = ({ trend, deltaPct, invert }) => {
  // For metrics where "down" is good (e.g. offline cameras, response time), invert color logic.
  if (trend === "flat" || !Number.isFinite(deltaPct)) {
    return (
      <span className="inline-flex items-center gap-0.5 font-mono text-[10px] text-muted-foreground" data-testid="kpi-trend">
        <Minus className="w-3 h-3" /> 0%
      </span>
    );
  }
  const isUp = trend === "up";
  const good = invert ? !isUp : isUp;
  const Icon = isUp ? TrendingUp : TrendingDown;
  const cls = good ? "sev-low" : "sev-critical";
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-[10px] ${cls}`} data-testid="kpi-trend">
      <Icon className="w-3 h-3" />
      {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
};

export const KpiCard = ({
  label,
  value,
  unit = "",
  sub,
  data,
  tone = "default",
  invertTrend = false,
  formatter,
  testid,
}) => {
  const t = TONE_CLASS[tone] || TONE_CLASS.default;
  const trend = data?.trend || "flat";
  const deltaPct = data?.delta_pct ?? 0;
  const spark = data?.sparkline || [];
  const display = formatter ? formatter(value) : value;

  return (
    <div
      className={`panel p-3 flex flex-col gap-2 border ${t.ring} transition-colors`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="micro-label leading-tight" data-testid={`${testid}-label`}>{label}</div>
        <TrendBadge trend={trend} deltaPct={deltaPct} invert={invertTrend} />
      </div>

      <div className="flex items-baseline gap-1">
        <div
          className={`font-display font-black tracking-tight text-2xl leading-none ${t.accent}`}
          data-testid={`${testid}-value`}
        >
          {display}
        </div>
        {unit && (
          <span className="font-mono text-[10px] text-muted-foreground">{unit}</span>
        )}
      </div>

      <Sparkline data={spark} color={t.fill} />

      {sub && (
        <div className="micro-label text-[9px] text-muted-foreground" data-testid={`${testid}-sub`}>
          {sub}
        </div>
      )}
    </div>
  );
};

export default KpiCard;
